// US states (+ DC) and Canadian provinces/territories, as a plain list.
//
// Lifted out of SelectState so a native <select> can use it too — on a phone
// that is the OS picker, which beats an autocomplete a driver has to type into.

export interface StateOrProvince {
  label: string
  value: string
  country: 'US' | 'CA'
}

const us = (label: string, value: string): StateOrProvince => ({
  label,
  value,
  country: 'US',
})

const ca = (label: string, value: string): StateOrProvince => ({
  label,
  value,
  country: 'CA',
})

export const US_STATES: StateOrProvince[] = [
  us('Alabama', 'AL'),
  us('Alaska', 'AK'),
  us('Arizona', 'AZ'),
  us('Arkansas', 'AR'),
  us('California', 'CA'),
  us('Colorado', 'CO'),
  us('Connecticut', 'CT'),
  us('Delaware', 'DE'),
  us('District of Columbia', 'DC'),
  us('Florida', 'FL'),
  us('Georgia', 'GA'),
  us('Hawaii', 'HI'),
  us('Idaho', 'ID'),
  us('Illinois', 'IL'),
  us('Indiana', 'IN'),
  us('Iowa', 'IA'),
  us('Kansas', 'KS'),
  us('Kentucky', 'KY'),
  us('Louisiana', 'LA'),
  us('Maine', 'ME'),
  us('Maryland', 'MD'),
  us('Massachusetts', 'MA'),
  us('Michigan', 'MI'),
  us('Minnesota', 'MN'),
  us('Mississippi', 'MS'),
  us('Missouri', 'MO'),
  us('Montana', 'MT'),
  us('Nebraska', 'NE'),
  us('Nevada', 'NV'),
  us('New Hampshire', 'NH'),
  us('New Jersey', 'NJ'),
  us('New Mexico', 'NM'),
  us('New York', 'NY'),
  us('North Carolina', 'NC'),
  us('North Dakota', 'ND'),
  us('Ohio', 'OH'),
  us('Oklahoma', 'OK'),
  us('Oregon', 'OR'),
  us('Pennsylvania', 'PA'),
  us('Rhode Island', 'RI'),
  us('South Carolina', 'SC'),
  us('South Dakota', 'SD'),
  us('Tennessee', 'TN'),
  us('Texas', 'TX'),
  us('Utah', 'UT'),
  us('Vermont', 'VT'),
  us('Virginia', 'VA'),
  us('Washington', 'WA'),
  us('West Virginia', 'WV'),
  us('Wisconsin', 'WI'),
  us('Wyoming', 'WY'),
]

// Province/territory codes do not collide with US state codes, so they can
// live in the same dropdowns and pass the same "valid state code" checks.
export const CANADIAN_PROVINCES: StateOrProvince[] = [
  ca('Alberta', 'AB'),
  ca('British Columbia', 'BC'),
  ca('Manitoba', 'MB'),
  ca('New Brunswick', 'NB'),
  ca('Newfoundland and Labrador', 'NL'),
  ca('Northwest Territories', 'NT'),
  ca('Nova Scotia', 'NS'),
  ca('Nunavut', 'NU'),
  ca('Ontario', 'ON'),
  ca('Prince Edward Island', 'PE'),
  ca('Quebec', 'QC'),
  ca('Saskatchewan', 'SK'),
  ca('Yukon', 'YT'),
]

export const STATE_AND_PROVINCE_OPTIONS: StateOrProvince[] = [
  ...US_STATES,
  ...CANADIAN_PROVINCES,
]

export const STATE_AND_PROVINCE_ABBREVIATIONS = STATE_AND_PROVINCE_OPTIONS.map(
  (region) => region.value
)

export const findLicenseRegion = (
  region?: string | null
): StateOrProvince | undefined => {
  if (!region) return undefined
  const normalized = region.trim().toUpperCase()
  return STATE_AND_PROVINCE_OPTIONS.find(
    (option) =>
      option.value.toUpperCase() === normalized ||
      option.label.toUpperCase() === normalized
  )
}

export const countryForLicenseRegion = (
  region?: string | null
): StateOrProvince['country'] | undefined => findLicenseRegion(region)?.country

const regionsIn = (country: StateOrProvince['country']) =>
  STATE_AND_PROVINCE_OPTIONS.filter((region) => region.country === country)

export const STATE_AND_PROVINCE_NAME_GROUPS = [
  {
    group: 'United States',
    items: regionsIn('US').map((region) => region.label),
  },
  {
    group: 'Canada',
    items: regionsIn('CA').map((region) => region.label),
  },
]

export const STATE_AND_PROVINCE_ABBREVIATION_GROUPS = [
  {
    group: 'United States',
    items: regionsIn('US').map((region) => region.value),
  },
  {
    group: 'Canada',
    items: regionsIn('CA').map((region) => region.value),
  },
]
